import apiClient from './client';
import type {
  TimeSlotResponse,
  TimeSlotListResponse,
  CreateTimeSlotRequest,
  UpdateTimeSlotRequest,
} from '../../types';

// Re-export types for convenience
export type { TimeSlotResponse, TimeSlotListResponse };

export const timeSlotsApi = {
  /**
   * Get all time slots for a cohort
   */
  list: async (cohortId: string): Promise<TimeSlotListResponse> => {
    return apiClient.get<TimeSlotListResponse>(`/api/cohorts/${cohortId}/time-slots`);
  },

  /**
   * Get a single time slot by ID
   */
  getById: async (cohortId: string, timeSlotId: string): Promise<TimeSlotResponse> => {
    return apiClient.get<TimeSlotResponse>(`/api/cohorts/${cohortId}/time-slots/${timeSlotId}`);
  },

  /**
   * Create a new time slot
   */
  create: async (cohortId: string, data: CreateTimeSlotRequest): Promise<TimeSlotResponse> => {
    return apiClient.post<TimeSlotResponse>(`/api/cohorts/${cohortId}/time-slots`, data);
  },

  /**
   * Update a time slot (day, time, timezone only - identifier and slot_number are immutable)
   */
  update: async (
    cohortId: string,
    timeSlotId: string,
    data: UpdateTimeSlotRequest,
  ): Promise<TimeSlotResponse> => {
    return apiClient.put<TimeSlotResponse>(`/api/cohorts/${cohortId}/time-slots/${timeSlotId}`, data);
  },

  /**
   * Get usage information for a time slot (for deletion confirmation)
   */
  getUsage: async (
    cohortId: string,
    timeSlotId: string,
  ): Promise<{
    isAssignedToGroup: boolean;
    availabilityRequestCount: number;
    canDelete: boolean;
  }> => {
    return apiClient.get<{
      isAssignedToGroup: boolean;
      availabilityRequestCount: number;
      canDelete: boolean;
    }>(`/api/cohorts/${cohortId}/time-slots/${timeSlotId}/usage`);
  },

  /**
   * Delete a time slot (soft delete)
   */
  delete: async (cohortId: string, timeSlotId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/time-slots/${timeSlotId}`);
  },

  /**
   * Validate time slot identifiers exist for a cohort
   */
  validateIdentifiers: async (
    cohortId: string,
    identifiers: string[],
  ): Promise<{ valid: boolean; not_found: string[] }> => {
    return apiClient.post<{ valid: boolean; not_found: string[] }>(
      `/api/cohorts/${cohortId}/time-slots/validate`,
      { identifiers },
    );
  },
};

export default timeSlotsApi;
